/**
 * What was last touched stays in view when the page's layout changes (its width crossing where it's laid out
 * otherwise: a look pinned beside it, the window resized). A region that a layout hides says where else it's shown,
 * as query changes in `data-elsewhere` (`tab=issues`; `tab=` for none: the page's first): when the last thing clicked
 * or focused is in a region the new layout hides, the page goes there.
 */
import { useEffect } from "preact/hooks";
import { setQuery } from "./router.ts";

/** The attribute naming where else a region is shown. */
const ELSEWHERE = "data-elsewhere";

let last: WeakRef<Element> | null = null;

/** Drawn (not hidden by its layout): it, or what holds it, has a box. */
const shown = (el: Element): boolean => el.getClientRects().length > 0;

/** After a change of layout: the region of what was last touched, if this layout hides it, is shown where it says. */
function follow(): void {
  const region = last?.deref()?.closest(`[${ELSEWHERE}]`);
  if (!region?.isConnected || shown(region)) {
    return;
  }
  last = null;
  const changes = new URLSearchParams(region.getAttribute(ELSEWHERE) ?? "");
  setQuery(Object.fromEntries(changes));
}

/** Keeps what was last touched in view as `page` (the page's box, whose width its layout follows) changes width. */
export function useKeepInView(page: { current: HTMLElement | null }): void {
  useEffect(() => {
    const el = page.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    // Only what's in the page: a look beside it (focused as it opens) isn't where one was.
    const touched = (e: Event) => {
      if (e.target instanceof Element && el.contains(e.target)) {
        last = new WeakRef(e.target);
      }
    };
    document.addEventListener("pointerdown", touched, true);
    document.addEventListener("focusin", touched, true);
    let width = el.clientWidth;
    const resized = new ResizeObserver(() => {
      if (el.clientWidth !== width) {
        width = el.clientWidth;
        follow();
      }
    });
    resized.observe(el);
    return () => {
      resized.disconnect();
      document.removeEventListener("pointerdown", touched, true);
      document.removeEventListener("focusin", touched, true);
    };
  }, [page]);
}

/** Where else a region is shown, as its attribute: `elsewhere("tab=issues")` spread on it. */
export const elsewhere = (query: string) => ({ [ELSEWHERE]: query });
